import { useEffect, useState } from "react";
import { ContactDamageReports } from "@/components/DamageReport";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { CONTACT_DETAIL, BOOKINGS } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Trash2, Mail, Phone, MapPin, ArrowLeft, Plus, Copy, RefreshCcw, Share2, Heart, Building2 } from "lucide-react";
import { toast } from "sonner";

function WishlistPanel({ contactId, properties }) {
  const [entries, setEntries] = useState([]);
  const [selectedProp, setSelectedProp] = useState("");
  const [minNights, setMinNights] = useState(3);
  const [horizonDays, setHorizonDays] = useState(90);

  const load = () =>
    api.get(`/contacts/${contactId}/wishlist`).then((r) => setEntries(r.data));
  useEffect(() => { load(); }, [contactId]);

  const add = async () => {
    if (!selectedProp) return toast.error("Pick a unit first");
    try {
      await api.post(`/contacts/${contactId}/wishlist`, {
        property_id: selectedProp,
        min_nights: Number(minNights),
        notify_within_days: Number(horizonDays),
      });
      setSelectedProp("");
      toast.success("Added to guest's wishlist");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remove this unit from the guest's wishlist?")) return;
    try {
      await api.delete(`/wishlist/${id}`);
      toast.success("Removed");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const availableProperties = properties.filter(
    (p) => !entries.some((e) => e.property_id === p.id),
  );
  return (
    <div className="ns-card p-5 mt-4" data-testid="contact-wishlist-panel">
      <div className="flex items-center gap-2 mb-3">
        <Heart className="w-4 h-4 text-teal-700" />
        <div className="ns-label">Wishlist</div>
        <span className="text-[11px] text-slate-400 ml-auto">
          Auto-email when a matching window opens
        </span>
      </div>
      <div className="grid grid-cols-[1fr_70px_70px_auto] gap-2 mb-3">
        <Select value={selectedProp} onValueChange={setSelectedProp}>
          <SelectTrigger data-testid="contact-wishlist-property" className="h-9 text-xs bg-white">
            <SelectValue placeholder={availableProperties.length ? "Unit…" : "All added"} />
          </SelectTrigger>
          <SelectContent>
            {availableProperties.map((p) => (
              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          data-testid="contact-wishlist-min-nights"
          type="number" min={1} max={30}
          value={minNights}
          onChange={(e) => setMinNights(e.target.value)}
          className="h-9 text-xs bg-white text-center"
          title="Minimum nights"
        />
        <Input
          data-testid="contact-wishlist-horizon"
          type="number" min={7} max={365}
          value={horizonDays}
          onChange={(e) => setHorizonDays(e.target.value)}
          className="h-9 text-xs bg-white text-center"
          title="Within next N days"
        />
        <Button
          data-testid="contact-wishlist-add"
          onClick={add}
          disabled={!selectedProp}
          className="h-9 px-3 text-xs rounded-full ns-btn-primary"
        >
          <Plus className="w-3 h-3" />
        </Button>
      </div>
      {entries.length === 0 ? (
        <div className="text-xs text-slate-400 italic py-3 text-center">
          No units on this guest's wishlist yet.
        </div>
      ) : (
        <div className="space-y-1.5">
          {entries.map((e) => (
            <div
              key={e.id}
              data-testid={`contact-wishlist-entry-${e.id}`}
              className="flex items-center gap-2 text-xs bg-stone-50 rounded-lg px-3 py-2"
            >
              <Building2 className="w-3 h-3 text-teal-700 shrink-0" />
              <div className="flex-1 min-w-0 truncate">{e.property_name}</div>
              <div className="text-[10px] text-slate-400 shrink-0">
                {e.min_nights}n · {e.notify_within_days}d
                {e.last_notified_at && ` · alerted ${new Date(e.last_notified_at).toLocaleDateString()}`}
              </div>
              <button
                data-testid={`contact-wishlist-remove-${e.id}`}
                onClick={() => remove(e.id)}
                className="text-slate-400 hover:text-red-600"
                title="Remove"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ReferralPanel({ contactId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/contacts/${contactId}/referral`).then((r) => setData(r.data));
  }, [contactId]);
  const copy = () => {
    if (!data?.share_url) return;
    navigator.clipboard.writeText(data.share_url);
    toast.success("Referral link copied");
  };
  const regen = async () => {
    if (!window.confirm("Regenerate this contact's referral link? The old one will stop working.")) return;
    try {
      await api.post(`/contacts/${contactId}/regenerate-referral`);
      const { data: fresh } = await api.get(`/contacts/${contactId}/referral`);
      setData(fresh);
      toast.success("New link ready to share");
    } catch (err) { toast.error(apiError(err)); }
  };
  if (!data) return null;
  return (
    <div className="ns-card p-5 mt-4">
      <div className="flex items-center gap-2 mb-3">
        <Share2 className="w-4 h-4 text-teal-700" />
        <div className="ns-label">Referral link</div>
      </div>
      <div className="flex gap-2">
        <Input readOnly value={data.share_url} data-testid="referral-url-input" className="bg-stone-50 text-sm" />
        <Button onClick={copy} data-testid="referral-copy-btn" variant="outline" className="rounded-full" title="Copy">
          <Copy className="w-4 h-4" />
        </Button>
        <Button onClick={regen} variant="ghost" className="rounded-full text-slate-500" title="Regenerate">
          <RefreshCcw className="w-4 h-4" />
        </Button>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-4 text-center">
        <div className="rounded-xl bg-teal-50 border border-teal-100 p-3">
          <div className="ns-label text-teal-800">Clicks</div>
          <div className="font-serif text-2xl text-slate-900 mt-1">{data.clicks}</div>
        </div>
        <div className="rounded-xl bg-violet-50 border border-violet-100 p-3">
          <div className="ns-label text-violet-800">Bookings driven</div>
          <div className="font-serif text-2xl text-slate-900 mt-1">{data.referred_bookings}</div>
        </div>
      </div>
      {data.referred_guests?.length > 0 && (
        <div className="mt-3 text-xs text-slate-500">
          Referred: {data.referred_guests.map((g) => `${g.first_name} ${g.last_name}`.trim()).join(", ")}
        </div>
      )}
    </div>
  );
}

export default function ContactDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [notes, setNotes] = useState([]);
  const [stays, setStays] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [properties, setProperties] = useState([]);
  const [note, setNote] = useState("");
  const [stayOpen, setStayOpen] = useState(false);
  const [stay, setStay] = useState({
    property_id: "",
    check_in: "",
    check_out: "",
    guests_count: 1,
    notes: "",
  });

  const load = () => {
    api.get(`/contacts/${id}`).then((r) => setC(r.data));
    api.get(`/contacts/${id}/notes`).then((r) => setNotes(r.data));
    api.get(`/bookings`, { params: { contact_id: id } }).then((r) => setStays(r.data));
    api.get(`/contacts/${id}/campaigns`).then((r) => setCampaigns(r.data));
    api.get(`/properties`).then((r) => setProperties(r.data));
  };
  useEffect(() => { load(); }, [id]);

  if (!c)
    return (
      <Layout title="Contact">
        <div className="text-slate-400">Loading…</div>
      </Layout>
    );

  const addNote = async () => {
    if (!note.trim()) return;
    await api.post(`/contacts/${id}/notes`, { body: note });
    setNote("");
    load();
  };

  const resubscribe = async () => {
    try {
      await api.post(`/contacts/${id}/resubscribe`);
      toast.success("Contact re-subscribed to mail-shots");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const del = async () => {
    if (!window.confirm("Delete this contact and all their notes / stays?")) return;
    await api.delete(`/contacts/${id}`);
    toast.success("Contact deleted");
    nav("/contacts");
  };

  const addStay = async () => {
    try {
      await api.post("/bookings", { ...stay, contact_id: id });
      toast.success("Stay recorded");
      setStayOpen(false);
      setStay({ property_id: "", check_in: "", check_out: "", guests_count: 1, notes: "" });
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <Layout
      title={`${c.first_name} ${c.last_name}`.trim() || c.company || "Contact"}
      subtitle={
        <span className="capitalize">
          {c.type}
          {c.trade ? ` · ${c.trade}` : ""} {c.company ? ` · ${c.company}` : ""}
        </span>
      }
      actions={
        <>
          <Button variant="ghost" onClick={() => nav("/contacts")} className="rounded-full">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <Button
            data-testid={CONTACT_DETAIL.deleteBtn}
            variant="ghost"
            onClick={del}
            className="rounded-full text-red-600 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4 mr-1" /> Delete
          </Button>
        </>
      }
    >
      <div data-testid={CONTACT_DETAIL.root}>
        {c.email_status && c.email_status !== "active" && (
          <div className="ns-card p-4 mb-5 border-l-4 border-red-400 bg-red-50/40 flex items-center justify-between gap-4">
            <div>
              <div className="text-sm text-red-800 font-medium capitalize">
                Email suppressed — {c.email_status}
              </div>
              <div className="text-xs text-red-600 mt-0.5">
                {c.email_status_reason ||
                  "This contact is excluded from mail-shots."}
              </div>
            </div>
            <Button
              variant="outline"
              onClick={resubscribe}
              className="rounded-full h-9 text-xs"
            >
              Re-subscribe
            </Button>
          </div>
        )}
        <div className="grid lg:grid-cols-[340px_1fr] gap-6">
        <div className="ns-card p-6 space-y-4 h-max">
          <div className="ns-label">Contact card</div>
          {c.email && (
            <div className="flex items-center gap-3 text-sm">
              <Mail className="w-4 h-4 text-teal-700" />
              <a href={`mailto:${c.email}`} className="text-slate-800 hover:text-teal-700">
                {c.email}
              </a>
            </div>
          )}
          {c.phone && (
            <div className="flex items-center gap-3 text-sm">
              <Phone className="w-4 h-4 text-teal-700" />
              <span>{c.phone}</span>
            </div>
          )}
          {c.address && (
            <div className="flex items-start gap-3 text-sm">
              <MapPin className="w-4 h-4 text-teal-700 mt-0.5" />
              <span>{c.address}</span>
            </div>
          )}
          <div className="ns-hairline my-2" />
          <div>
            <div className="ns-label mb-2">Tags</div>
            <div className="flex flex-wrap gap-1.5">
              {(c.tags || []).length === 0 && (
                <span className="text-xs text-slate-400">No tags yet.</span>
              )}
              {(c.tags || []).map((t) => (
                <Badge
                  key={t}
                  className="bg-teal-50 text-teal-800 hover:bg-teal-100 rounded-full font-normal"
                >
                  {t}
                </Badge>
              ))}
            </div>
          </div>
          {c.notes && (
            <>
              <div className="ns-hairline my-2" />
              <div className="ns-label mb-2">Summary</div>
              <p className="text-sm text-slate-600 whitespace-pre-wrap">{c.notes}</p>
            </>
          )}
        </div>
        {(c.type === "guest") && <ReferralPanel contactId={c.id} />}
        {(c.type === "guest") && <WishlistPanel contactId={c.id} properties={properties} />}

        <div className="ns-card p-6">
          <Tabs defaultValue="stays">
            <TabsList className="bg-stone-100 rounded-full">
              <TabsTrigger
                data-testid={CONTACT_DETAIL.tabStays}
                value="stays"
                className="rounded-full data-[state=active]:bg-white"
              >
                Stays
              </TabsTrigger>
              <TabsTrigger
                data-testid={CONTACT_DETAIL.tabNotes}
                value="notes"
                className="rounded-full data-[state=active]:bg-white"
              >
                Notes & activity
              </TabsTrigger>
              <TabsTrigger
                data-testid={CONTACT_DETAIL.tabCampaigns}
                value="campaigns"
                className="rounded-full data-[state=active]:bg-white"
              >
                Campaigns
              </TabsTrigger>
            </TabsList>

            <TabsContent value="stays" className="mt-6">
              <div className="flex justify-between items-center mb-4">
                <div className="ns-label">Stay history</div>
                <Button
                  data-testid={CONTACT_DETAIL.addStayBtn}
                  onClick={() => setStayOpen(true)}
                  className="rounded-full ns-btn-primary h-9 px-4 text-xs"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" /> Record stay
                </Button>
              </div>
              <div className="space-y-2">
                {stays.length === 0 && (
                  <div className="text-sm text-slate-400 py-10 text-center">
                    No stays recorded for this contact yet.
                  </div>
                )}
                {stays.map((s) => (
                  <div
                    key={s.id}
                    className="p-4 border border-slate-100 rounded-xl flex items-center justify-between hover:border-teal-200"
                  >
                    <div>
                      <div className="font-medium">{s.property_name}</div>
                      <div className="text-xs text-slate-500">
                        {s.check_in} → {s.check_out} · {s.guests_count} guest(s)
                      </div>
                      {s.notes && (
                        <div className="text-xs text-slate-500 mt-1">{s.notes}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="notes" className="mt-6">
              <div className="ns-label mb-3">Add a note</div>
              <div className="flex gap-2">
                <Textarea
                  data-testid={CONTACT_DETAIL.noteInput}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={2}
                  className="bg-white"
                  placeholder="Left thank-you card; guest loved the balcony…"
                />
                <Button
                  data-testid={CONTACT_DETAIL.noteAddBtn}
                  onClick={addNote}
                  className="rounded-full ns-btn-primary self-end px-5"
                >
                  Add
                </Button>
              </div>
              <div className="ns-hairline my-6" />
              <div className="space-y-3">
                {notes.length === 0 && (
                  <div className="text-sm text-slate-400 py-6 text-center">
                    No notes yet.
                  </div>
                )}
                {notes.map((n) => (
                  <div key={n.id} className="p-4 rounded-xl bg-stone-50 border border-stone-100">
                    <div className="text-sm text-slate-800 whitespace-pre-wrap">{n.body}</div>
                    <div className="text-[11px] text-slate-400 mt-2">
                      {new Date(n.created_at).toLocaleString()} · {n.author}
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="campaigns" className="mt-6">
              <div className="space-y-2">
                {campaigns.length === 0 && (
                  <div className="text-sm text-slate-400 py-10 text-center">
                    This contact hasn&apos;t received any campaigns yet.
                  </div>
                )}
                {campaigns.map((c) => (
                  <Link
                    to={`/campaigns`}
                    key={c.id}
                    className="p-4 border border-slate-100 rounded-xl flex items-center justify-between hover:border-teal-200"
                  >
                    <div>
                      <div className="font-medium">{c.name}</div>
                      <div className="text-xs text-slate-500">{c.subject}</div>
                    </div>
                    <div className="text-xs">
                      {c.sends?.[0]?.status === "sent" ? (
                        <span className="text-teal-700">Delivered</span>
                      ) : (
                        <span className="text-red-600">Failed</span>
                      )}
                      <div className="text-[10px] text-slate-400">
                        {new Date(c.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
        </div>
      </div>

      <Dialog open={stayOpen} onOpenChange={setStayOpen}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Record a stay</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="ns-label">Property</Label>
              <Select
                value={stay.property_id}
                onValueChange={(v) => setStay({ ...stay, property_id: v })}
              >
                <SelectTrigger data-testid={BOOKINGS.fieldProperty} className="mt-2 h-11 bg-white">
                  <SelectValue placeholder="Select a unit" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="ns-label">Check-in</Label>
                <Input
                  data-testid={BOOKINGS.fieldCheckIn}
                  type="date"
                  className="mt-2 h-11 bg-white"
                  value={stay.check_in}
                  onChange={(e) => setStay({ ...stay, check_in: e.target.value })}
                />
              </div>
              <div>
                <Label className="ns-label">Check-out</Label>
                <Input
                  data-testid={BOOKINGS.fieldCheckOut}
                  type="date"
                  className="mt-2 h-11 bg-white"
                  value={stay.check_out}
                  onChange={(e) => setStay({ ...stay, check_out: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label className="ns-label">Guests</Label>
              <Input
                type="number"
                min={1}
                className="mt-2 h-11 bg-white"
                value={stay.guests_count}
                onChange={(e) => setStay({ ...stay, guests_count: Number(e.target.value) })}
              />
            </div>
            <div>
              <Label className="ns-label">Notes</Label>
              <Textarea
                className="mt-2 bg-white"
                rows={2}
                value={stay.notes}
                onChange={(e) => setStay({ ...stay, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setStayOpen(false)}>
              Cancel
            </Button>
            <Button
              data-testid={BOOKINGS.saveBtn}
              onClick={addStay}
              className="rounded-full ns-btn-primary px-6"
            >
              Save stay
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ContactDamageReports contactId={id} />
    </Layout>
  );
}
